import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to determine if teams should be displayed in one or two rows
 * based on actual available space rather than just team count
 *
 * @param {Object} params Hook parameters
 * @param {number} params.teamCount Number of teams to display
 * @param {number} params.minTeamWidth Minimum width needed for each team in pixels
 * @param {number} params.breakToTwoRowsAtTeams Optional count to force two rows (default: 6)
 * @returns {Object} Object containing layout information
 * @returns {boolean} Object.useTwoRows Whether to use two row layout
 * @returns {React.RefObject} Object.containerRef Ref to attach to container element
 */
export function useTeamLayoutMeasure({
  teamCount,
  minTeamWidth = 130,
  breakToTwoRowsAtTeams = 6,
}) {
  const containerRef = useRef(null);
  const [useTwoRows, setUseTwoRows] = useState(false);

  useEffect(() => {
    // Function to measure and decide on layout
    const determineLayout = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const totalWidthNeeded = teamCount * minTeamWidth;

      // Always use two rows if team count is at or above the break point
      if (teamCount >= breakToTwoRowsAtTeams) {
        setUseTwoRows(true);
        return;
      }

      // Determine if we need two rows based on available space
      const needsTwoRows = totalWidthNeeded > containerWidth;
      setUseTwoRows(needsTwoRows);
    };

    // Measure on initial render
    determineLayout();

    // Set up resize observer to track container size changes
    // Fall back to window resize for older browsers (Safari < 13.1)
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(determineLayout);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      return () => {
        if (containerRef.current) {
          resizeObserver.unobserve(containerRef.current);
        }
        resizeObserver.disconnect();
      };
    } else {
      window.addEventListener('resize', determineLayout);
      return () => {
        window.removeEventListener('resize', determineLayout);
      };
    }
  }, [teamCount, minTeamWidth, breakToTwoRowsAtTeams]);

  return { useTwoRows, containerRef };
}

export default useTeamLayoutMeasure;
